import { supabase } from "@/integrations/supabase/client";
import { Movie } from "@/types/movie";
import { useAuth } from "@/context/AuthContext";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { getMovieEnriched } from "./universalMovieService";

interface DirectorFilmographyResponse {
  movies: Movie[];
  error?: string;
}

/**
 * PHASE 2: Enhanced director filmography service with proper metadata enrichment
 * Removes conditional enrichment logic - always enrich for consistency
 */
export const fetchDirectorFilmography = async (
  directorName: string,
  uid?: string
): Promise<Movie[]> => {
  try {
    console.log(`🎬 [DirectorFilmography] Fetching filmography for: ${directorName}`);
    
    const { data, error } = await supabase.functions.invoke<DirectorFilmographyResponse>(
      'get_director_filmography',
      {
        body: { directorName, uid }
      }
    );

    if (error) {
      console.error(`🎬 [DirectorFilmography] Error fetching filmography for ${directorName}:`, error);
      return [];
    }

    const edgeFunctionMovies = data?.movies || [];
    console.log(`🎬 [DirectorFilmography] Retrieved ${edgeFunctionMovies.length} movies for ${directorName} from edge function`);
    
    if (edgeFunctionMovies.length === 0) {
      return [];
    }

    // PHASE 2: Always enrich movies for consistency, regardless of existing metadata
    const sortedMovies = edgeFunctionMovies.sort((a, b) => {
      const popularityA = a.popularity || 0;
      const popularityB = b.popularity || 0;
      return popularityB - popularityA;
    });
    
    const moviesToEnrich = sortedMovies.slice(0, 15);
    console.log(`🎬 [DirectorFilmography] PHASE 2: Always enriching ${moviesToEnrich.length} movies for consistency`);

    // PHASE 2: Remove conditional enrichment - always enrich for universal metadata consistency
    const enrichedMovies = await Promise.allSettled(
      moviesToEnrich.map(async (movie) => {
        try {
          console.log(`🎬 [DirectorFilmography] PHASE 2: Universal enrichment for: ${movie.title}`);
          
          // Always use universal enrichment service for complete metadata
          const enrichedMovie = await getMovieEnriched(movie.tmdbId, uid);
          
          // Preserve director name and source from filmography, merge with enriched data
          const finalMovie: Movie = {
            ...enrichedMovie,
            director: directorName, // Preserve from filmography
            source: 'director' as const,
            // Preserve user flags from edge function (already includes user state)
            favorite: movie.favorite || enrichedMovie.favorite,
            planToWatch: movie.planToWatch || enrichedMovie.planToWatch
          };
          
          console.log(`🎬 [DirectorFilmography] PHASE 2: Universal enrichment complete: ${movie.title} - tags: ${finalMovie.tags?.join(', ')}`);
          return finalMovie;
        } catch (error) {
          console.warn(`🎬 [DirectorFilmography] PHASE 2: Enrichment failed for ${movie.title}, using edge function data:`, error);
          
          // PHASE 2: Only use edge function data if it has proper genres
          if (!movie.tags || movie.tags.length === 0) {
            console.error(`🎬 [DirectorFilmography] PHASE 2: Skipping ${movie.title} - no genre data available`);
            throw error; // Let this fail rather than return incomplete data
          }
          
          return {
            ...movie,
            director: directorName,
            source: 'director' as const
          };
        }
      })
    );

    // Process settled promises and get successful results
    const finalMovies = enrichedMovies.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        console.warn(`🎬 [DirectorFilmography] PHASE 2: Failed to process movie ${index}:`, result.reason);
        return null;
      }
    }).filter((movie): movie is Movie => movie !== null);

    console.log(`🎬 [DirectorFilmography] PHASE 2: Returning ${finalMovies.length} movies for ${directorName} with universal metadata`);
    return finalMovies;
  } catch (error) {
    console.error(`🎬 [DirectorFilmography] Failed to fetch filmography for ${directorName}:`, error);
    return [];
  }
};

/**
 * React Query hook for director filmography with caching
 */
export const useDirectorFilmography = (directorName: string, enabled: boolean = true) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['director-filmography', directorName, user?.id],
    queryFn: () => fetchDirectorFilmography(directorName, user?.id),
    enabled: enabled && !!directorName,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
    refetchOnWindowFocus: false
  });
};

/**
 * Hook to manage multiple director filmographies
 */
export const useMultipleDirectorFilmographies = (directorNames: string[]) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const queries = useQueries({
    queries: directorNames.map(directorName => ({
      queryKey: ['director-filmography', directorName, user?.id],
      queryFn: () => fetchDirectorFilmography(directorName, user?.id),
      enabled: !!directorName,
      staleTime: 1000 * 60 * 30,
      gcTime: 1000 * 60 * 60,
      retry: 1,
      refetchOnWindowFocus: false
    }))
  });

  // Clear filmography cache for a specific director
  const clearDirectorFilmography = (directorName: string) => {
    queryClient.removeQueries({
      queryKey: ['director-filmography', directorName]
    });
  };

  // Clear all filmography caches
  const clearAllFilmographies = () => {
    queryClient.removeQueries({
      queryKey: ['director-filmography']
    });
  };

  // Combine all filmography movies - now all have complete metadata
  const allFilmographyMovies = queries.reduce<Movie[]>((acc, query) => {
    if (query.data) {
      acc.push(...query.data);
    }
    return acc;
  }, []);

  // Check if any queries are loading
  const isLoading = queries.some(query => query.isLoading);
  
  // Check if any queries have errors
  const hasError = queries.some(query => query.error);

  return {
    filmographyMovies: allFilmographyMovies,
    isLoading,
    hasError,
    queries,
    clearDirectorFilmography,
    clearAllFilmographies
  };
};
